<script setup lang="ts">
import { formatDocument } from "../../helpers";
import type { CollectionItem, CollectionItemWithId } from '@aeriajs/types'

definePage({
  meta: {
    title: "Vendas",
    icon: "file",
  },
});

const orderStore = useStore("order");
const metaStore = useStore("meta");

const formSchema = {
  attendants: {
    description: "Atendentes",
    type: "array",
    items: {
      $ref: "user",
      indexes: ["name", "email"],
    },
    isReference: true,
    isFile: false,
    referencedCollection: "user",
  },
  broadcast: {
    description: "Broadcast",
    $ref: "broadcast",
    indexes: ["name"],
    isReference: true,
    isFile: false,
    referencedCollection: "broadcast",
  },
};
const formData = reactive<any>({});

async function createTasks() {
  if (orderStore.selected.length == 0) {
    metaStore.$actions.spawnModal({
      title: "Ops",
      body: "Selecione os pedidos",
    });
  }

  if (!formData.attendants || formData.attendants.length == 0) {
    metaStore.$actions.spawnModal({
      title: "Ops",
      body: "Selecione os atendentes",
    });
  }

  if (!formData.broadcast) {
    metaStore.$actions.spawnModal({
      title: "Ops",
      body: "Selecione o broadcast",
    });
  }

  const { result } = await aeria.order.createTasks.POST({
    orders: orderStore.selected,
    ...formData,
  });

  if (result == "ok") {
    metaStore.$actions.spawnToast({
      text: "As tarefas foram criadas com sucesso",
    });
  } else {
    metaStore.$actions.spawnModal({
      title: "Ops",
      body: "Ocorreu um erro ao criar as tarefas",
    });
  }

  orderStore.selected = [];
  orderStore.$actions.closeModal();
  orderStore.$actions.clearItem();

  orderStore.$actions.getAll({
    filters: {
      pending: true,
    },
  });
}

const currentFunnelPostbacks = ref<CollectionItemWithId<'funnelPostbackLog'>[]>([]) 
const eventsPanel = ref<boolean>(false);
const handleUiEvent = async (event: any) => {
  if (event.name === "funnelLog") {
    const logs = await aeria.order.getOrderPostbacks.POST({
      orderId: event.params._id,
    })


    currentFunnelPostbacks.value = logs

    eventsPanel.value = true
  }
};
</script>

<template>
  <aeria-panel close-hint float title="Log de funis" v-model="eventsPanel" @overlay-click="() => eventsPanel = false">
    <aeria-table v-if="currentFunnelPostbacks?.length > 0">
      <template #thead>
        <div class="font-bold">Tentativas de acionar um funil:</div>
        <tr>
          <th>Evento</th>
          <th>Resultado</th>
          <th>Data</th>
        </tr>
      </template>
      <template #tbody>
        <tr v-for="funnelPostback in currentFunnelPostbacks" :key="funnelPostback._id">
          <td>{{ t(funnelPostback.payload.event, { capitalize: true }) }}</td>
          <td>{{ 'success' in funnelPostback ? funnelPostback.success ? 'Funil encontrado' : (funnelPostback.error || 'Nenhum funil encontrado') : 'Indeterminado' }}</td>
          <td>{{ new Date(funnelPostback.created_at).toLocaleString() }}</td>
        </tr>
      </template>
    </aeria-table>
    <div v-else>
      Nenhum log encontrado
    </div>
  </aeria-panel>

  <aeria-crud collection="order" @ui-event="handleUiEvent">
    <template #row-local_id="{ row, column }">
      <div class="flex flex-col gap-1 max-w-[80pt]">
        <div>{{ row[column] }}</div>
        <div class="break-words whitespace-normal text-[8.5pt] opacity-60">
          {{ row.token }}
        </div>
      </div>
    </template>

    <template #row-customer="{ row, column }">
      <div class="flex flex-col gap-1">
        <div>{{ row[column].name }}</div>
        <div class="text-[8.5pt] opacity-60">
          {{ formatDocument(row[column].document) }}
        </div>
      </div>
    </template>

    <template #row-status="{ row, column }">
      <status-badge :color="orderStore.$actions.getComputedColor(row[column])">
        {{ t(row[column] || "-") }}
      </status-badge>
    </template>

    <template #row-products="{ row, column }">
      <span v-if="!row.manual_posting" class="flex items-start">
        <extra-icon :name="row.integration.platform"></extra-icon>
        <!-- Por algum motivo o tailwindcss não tá aplicando o space-x -->
        <div class="flex flex-col items-start ml-1">
          <div>
            {{ row[column].map((product) => product.name || "??").join(", ") }}
          </div>
          <div class="text-[8.5pt] opacity-60">
            {{ row[column].map((product) => product.code).join(", ") }}
          </div>
        </div>
      </span>

      <div v-else>
        {{ row.package_content }}
      </div>
    </template>

    <template #row-plans="{ row, column }">
      <div class="flex flex-col">
        <div class="text-wrap max-w-[20rem]">
          {{ row[column].map((plan) => plan.name || "??").join(", ") }}
        </div>
        <div class="text-[8.5pt] opacity-60">
          {{ row[column].map((plan) => plan.code).join(", ") }}
        </div>
      </div>
    </template>

    <template #row-tracking_code="{ row, column }">
      <div class="flex items-center gap-1">
        <extra-icon
          v-if="row[column]"
          :name="row.shipping_profile?.provider"
        ></extra-icon>
        <div>{{ row[column] || "-" }}</div>
      </div>
    </template>

    <template #row-date_updated="{ row, column }">
      <div class="text-[8.5pt]">
        {{ new Date(row[column]).toLocaleString() }}
      </div>
    </template>

    <template #row-created_at="{ row, column }">
      <div class="text-[8.5pt]">
        {{ new Date(row[column]).toLocaleString() }}
      </div>
    </template>
  </aeria-crud>

  <aeria-panel
    float
    title="Criar tarefas"
    v-model="orderStore.modal"
    @overlay-click="() => orderStore.$actions.closeModal()"
    :key="orderStore.item?._id"
  >
    <aeria-form :form="formSchema" v-model="formData"></aeria-form>
    <template #footer>
      <aeria-button
        variant="transparent"
        @click="() => orderStore.$actions.closeModal()"
      >
        Cancelar
      </aeria-button>
      <aeria-button large @click="createTasks"> Criar </aeria-button>
    </template>
  </aeria-panel>
</template>
